import React from "react";
import FieldCard from "./FieldCard";
import InputField from "./InputField";

function TerraceSectionCard({ title, children }) {
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 p-4">
      <div className="mb-3 text-sm font-semibold text-white">{title}</div>
      {children}
    </div>
  );
}

function createDefaultTerrace(base = {}) {
  return {
    id: crypto.randomUUID(),
    name: "",
    lengthM: "",
    widthM: "",
    areaM2: "",
    terraceMaterial: "wood",
    stepCount: 0,
    railingRequired: false,
    stepsRequired: false,
    groundPrepRequired: false,
    ...base,
  };
}

function normalizeTerraceForEditor(section = {}) {
  return createDefaultTerrace({
    ...section,
    id: section.id || crypto.randomUUID(),
    stepCount:
      section.stepCount === "" ? "" : Number(section.stepCount ?? 0) || 0,
    railingRequired: !!section.railingRequired,
    stepsRequired: !!section.stepsRequired,
    groundPrepRequired: !!section.groundPrepRequired,
    terraceMaterial: section.terraceMaterial || "wood",
  });
}

export default function TerraceEditor({
  parsedData,
  setParsedData,
  errors = {},
}) {
  const terraces =
    Array.isArray(parsedData?.terraces) && parsedData.terraces.length
      ? parsedData.terraces.map(normalizeTerraceForEditor)
      : [
          normalizeTerraceForEditor({
            name: "",
            lengthM: parsedData.terraceLengthM ?? "",
            widthM: parsedData.terraceWidthM ?? "",
            areaM2: parsedData.terraceAreaM2 ?? "",
            terraceMaterial: parsedData.terraceMaterial || "wood",
            stepCount: parsedData.stepCount ?? 0,
            railingRequired: !!parsedData.railingRequired,
            stepsRequired: !!parsedData.stepsRequired,
            groundPrepRequired: !!parsedData.groundPrepRequired,
          }),
        ];

  const setTerraces = (nextTerraces) => {
    const normalizedTerraces = (Array.isArray(nextTerraces) ? nextTerraces : []).map(
      normalizeTerraceForEditor
    );

    setParsedData((prev) => ({
      ...prev,
      projectType: "terrace",
      primaryProjectType: "terrace",
      projectTypes: ["terrace"],
      terraces: normalizedTerraces,
      terraceLengthM: normalizedTerraces[0]?.lengthM ?? "",
      terraceWidthM: normalizedTerraces[0]?.widthM ?? "",
      terraceAreaM2: normalizedTerraces[0]?.areaM2 ?? "",
      terraceMaterial: normalizedTerraces[0]?.terraceMaterial || "wood",
      stepCount: normalizedTerraces[0]?.stepCount ?? 0,
      railingRequired: !!normalizedTerraces[0]?.railingRequired,
      stepsRequired: !!normalizedTerraces[0]?.stepsRequired,
      groundPrepRequired: !!normalizedTerraces[0]?.groundPrepRequired,
    }));
  };

  const addTerrace = () => {
    setTerraces([...terraces, createDefaultTerrace()]);
  };

  const updateTerrace = (index, field, value) => {
    setTerraces(
      terraces.map((section, i) =>
        i === index
          ? normalizeTerraceForEditor({ ...section, [field]: value })
          : section
      )
    );
  };

  const removeTerrace = (index) => {
    const nextTerraces = terraces.filter((_, i) => i !== index);

    setTerraces(
      nextTerraces.length ? nextTerraces : [createDefaultTerrace()]
    );
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-gray-400">
        Define one or more terrace sections. Enter area directly or use length
        and width for each section.
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={addTerrace}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Add terrace section
        </button>
      </div>

      <div className="space-y-4">
        {terraces.map((section, index) => (
          <TerraceSectionCard
            key={section.id || `terrace-${index}`}
            title={section.name?.trim() || `Terrace section ${index + 1}`}
          >
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <FieldCard
                label="Section name"
                warning="Optional internal name like Main terrace, Entrance deck, or Balcony."
              >
                <InputField
                  value={section.name ?? ""}
                  onChange={(e) => updateTerrace(index, "name", e.target.value)}
                  placeholder="e.g. Main terrace"
                />
              </FieldCard>

              <FieldCard
                label="Terrace length"
                unit="m"
                error={
                  errors[`terraces.${index}`] ||
                  (index === 0 ? errors.terraceAreaM2 : "")
                }
                warning="Optional if you already know the total area."
              >
                <InputField
                  type="number"
                  value={section.lengthM ?? ""}
                  onChange={(e) =>
                    updateTerrace(
                      index,
                      "lengthM",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="e.g. 6"
                />
              </FieldCard>

              <FieldCard
                label="Terrace width"
                unit="m"
                error={
                  errors[`terraces.${index}`] ||
                  (index === 0 ? errors.terraceAreaM2 : "")
                }
                warning="Optional if you already know the total area."
              >
                <InputField
                  type="number"
                  value={section.widthM ?? ""}
                  onChange={(e) =>
                    updateTerrace(
                      index,
                      "widthM",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="e.g. 4"
                />
              </FieldCard>

              <FieldCard
                label="Terrace area"
                unit="m²"
                error={
                  errors[`terraces.${index}`] ||
                  (index === 0 ? errors.terraceAreaM2 : "")
                }
                warning="Use this if you know the full area already."
              >
                <InputField
                  type="number"
                  value={section.areaM2 ?? ""}
                  onChange={(e) =>
                    updateTerrace(
                      index,
                      "areaM2",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="e.g. 24"
                />
              </FieldCard>

              <FieldCard
                label="Decking type"
                warning="Select the main surface material."
              >
                <select
                  value={section.terraceMaterial || "wood"}
                  onChange={(e) =>
                    updateTerrace(index, "terraceMaterial", e.target.value)
                  }
                  className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="wood">Wood decking</option>
                  <option value="composite">Composite decking</option>
                  <option value="tile">Tiles / slabs</option>
                  <option value="other">Other</option>
                </select>
              </FieldCard>

              <FieldCard
                label="Railing required?"
                warning="Enable if this section needs railings."
              >
                <select
                  value={section.railingRequired ? "yes" : "no"}
                  onChange={(e) =>
                    updateTerrace(
                      index,
                      "railingRequired",
                      e.target.value === "yes"
                    )
                  }
                  className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </FieldCard>

              <FieldCard
                label="Steps required?"
                warning="Enable if stairs or access steps need to be built."
              >
                <select
                  value={section.stepsRequired ? "yes" : "no"}
                  onChange={(e) =>
                    updateTerrace(
                      index,
                      "stepsRequired",
                      e.target.value === "yes"
                    )
                  }
                  className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </FieldCard>

              <FieldCard
                label="Step count"
                warning="Used when steps are required."
              >
                <InputField
                  type="number"
                  value={section.stepCount ?? 0}
                  onChange={(e) =>
                    updateTerrace(
                      index,
                      "stepCount",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="e.g. 3"
                />
              </FieldCard>

              <FieldCard
                label="Ground prep required?"
                warning="Enable if base preparation or ground work is needed."
              >
                <select
                  value={section.groundPrepRequired ? "yes" : "no"}
                  onChange={(e) =>
                    updateTerrace(
                      index,
                      "groundPrepRequired",
                      e.target.value === "yes"
                    )
                  }
                  className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </FieldCard>

              {index === 0 && (
                <FieldCard
                  label="Finish level"
                  warning="Use premium if the project requires a higher finish level."
                >
                  <select
                    value={parsedData.finishLevel || "standard"}
                    onChange={(e) =>
                      setParsedData((prev) => ({
                        ...prev,
                        projectType: "terrace",
                        primaryProjectType: "terrace",
                        projectTypes: ["terrace"],
                        finishLevel: e.target.value,
                        terraces,
                      }))
                    }
                    className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                  </select>
                </FieldCard>
              )}
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => removeTerrace(index)}
                className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Remove section
              </button>
            </div>
          </TerraceSectionCard>
        ))}
      </div>
    </div>
  );
}